// O card da oportunidade no quadro: o resumo, o risco, o responsável e as ações. (#880)
//
// ── O desenho de 26/10/2026 ─────────────────────────────────────────────────
//
// Em repouso o card diz só o que se lê de relance: quem, o quê, quanto, por
// onde e o que fazer agora. As ações (mover, conversa, tarefa, passar, editar,
// excluir) aparecem ao passar o mouse ou ao focar o card — continuam no DOM, e o
// teclado e o leitor de tela chegam a elas como antes. O "Mover" continua sendo
// a alternativa ao arrasto (§19).

import { useState, type PointerEvent as EventoDePonteiro, type ReactNode } from 'react';
import type { AtendimentoDoSisac, CardDoFunil } from '@cliqcrm/contracts';
import { dinheiro, dinheiroComCentavos } from '@/shared/lib/formato';
import {
  PELA_AGENDA,
  diaNaClinica,
  nomeDoAtendimento,
  quandoNaClinica,
  valorEhPrevisto,
} from '@/entities/saude/atendimento';
import { Atribuir } from '@/features/atendimento/passar-para-outra-pessoa';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import { Avatar, Cartao, Icone } from '@/shared/ui';

const MOTIVO_DO_RISCO: Record<string, string> = {
  sem_interacao: 'sem interação',
  etapa_parada: 'parada nesta etapa',
};

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * Quando é a tarefa, como se fala: "hoje", "amanhã", o dia da semana até uma
 * semana adiante, e a data depois disso. Atrasada diz "atrasada".
 */
export function quandoDaTarefa(prazo: string | null, agora = new Date()): string | null {
  if (!prazo) return null;
  const dia = new Date(prazo);
  const meiaNoite = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((meiaNoite(dia) - meiaNoite(agora)) / 86_400_000);
  if (dias < 0) return 'atrasada';
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'amanhã';
  if (dias < 7) return DIAS[dia.getDay()]!;
  return dia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** O selo do risco, com o número: "Parada há 4 dias", "Sem interação há 9 dias". */
function seloDoRisco(risco: CardDoFunil['risco']): string | null {
  if (!risco.em_risco) return null;
  if (risco.sinais.includes('etapa_parada') && risco.dias_na_etapa !== null) {
    return `Parada há ${risco.dias_na_etapa} ${risco.dias_na_etapa === 1 ? 'dia' : 'dias'}`;
  }
  if (risco.dias_sem_interacao !== null) {
    return `Sem interação há ${risco.dias_sem_interacao} ${
      risco.dias_sem_interacao === 1 ? 'dia' : 'dias'
    }`;
  }
  return 'Em risco';
}

export function Card({
  card,
  atendimento = null,
  resultado = null,
  aoSegurar,
  noAr = false,
  etapas,
  outrosFunis,
  emMovimento,
  aoMover,
  aoAbrirConversa,
  aoCriarTarefa,
  aoEditar,
  aoExcluir,
  aoAbrir,
}: {
  card: CardDoFunil;
  /** O que o card é no SISAC (#1151); `null` fora da clínica ou enquanto não chega. */
  atendimento?: AtendimentoDoSisac | null;
  /** A coluna é de ganho ou de perda: o card mostra o selo. */
  resultado?: 'ganho' | 'perdido' | null;
  /** O começo do arrasto com gravidade (`arrasto-com-gravidade.tsx`). */
  aoSegurar?: (evento: EventoDePonteiro<HTMLElement>, card: CardDoFunil) => void;
  /** O card está no ar: no lugar dele fica a sombra. */
  noAr?: boolean;
  etapas: readonly { etapa: string; rotulo: string }[];
  outrosFunis: readonly { nome: string; etapas: readonly { etapa: string; rotulo: string }[] }[];
  emMovimento: boolean;
  aoMover: (card: CardDoFunil, etapa: string) => void;
  aoAbrirConversa: (conversaId: string) => void;
  aoCriarTarefa: (card: CardDoFunil) => void;
  aoEditar: (card: CardDoFunil) => void;
  aoExcluir: (card: CardDoFunil) => void;
  aoAbrir: (card: CardDoFunil) => void;
}): ReactNode {
  // Valor só quando há valor: "R$ 0" num selo verde comemora um negócio que
  // ainda não tem preço.
  const valor = card.valor && card.valor.quantia_micros > 0 ? dinheiro(card.valor) : null;
  const podeMover = card.id !== null;
  const [passando, definirPassando] = useState(false);

  const interacao = idadeEmPalavras(card.ultima_interacao_em);
  const risco = seloDoRisco(card.risco);
  const canal = nomeDoCanal(card.canal);
  const tarefa = card.proxima_tarefa ?? null;
  const quando = tarefa ? quandoDaTarefa(tarefa.prazo_em) : null;
  const nome = card.cliente?.nome_exibicao ?? card.titulo ?? 'Sem título';
  const motivoDoRisco = card.risco.em_risco
    ? card.risco.sinais.map((sinal) => MOTIVO_DO_RISCO[sinal] ?? sinal).join(', ') +
      (card.risco.dias_sem_interacao !== null ? ` (${card.risco.dias_sem_interacao} dias)` : '')
    : null;
  const travado = 'Esta oportunidade não pode ser alterada agora.';

  return (
    <Cartao
      como="article"
      variante="arrastavel"
      parte="card-op"
      movendo={emMovimento}
      data-card-id={card.referencia_externa}
      data-no-ar={noAr ? 'sim' : undefined}
      data-risco={risco ? 'sim' : undefined}
      // O arrasto é por ponteiro, e não o nativo: o nativo desenha uma foto
      // fixa do card, que não balança nem se encaixa (26/10/2026).
      {...(podeMover && aoSegurar
        ? { onPointerDown: (evento: EventoDePonteiro<HTMLElement>) => aoSegurar(evento, card) }
        : {})}
      /*
       * O clique no card abre a ficha — menos quando cai num controle.
       *
       * O card tem select de mover e quatro botões dentro. Sem esta guarda,
       * escolher "Passar para" abriria a ficha por baixo do menu, e o clique
       * teria feito duas coisas de uma vez.
       */
      onClick={(evento) => {
        const alvo = evento.target as HTMLElement;
        if (alvo.closest('button, select, label, a, input, textarea')) return;
        aoAbrir(card);
      }}
    >
      <div className="card-op__cabeca">
        {/*
          O nome é BOTÃO, e não só texto dentro de uma área clicável: é o
          caminho por teclado e o que o leitor de tela anuncia como "abrir".
        */}
        <button
          type="button"
          className="card-op__titulo"
          title={nome}
          onClick={() => aoAbrir(card)}
        >
          {nome}
        </button>
        {card.cliente?.nome_exibicao && card.titulo && (
          <span className="card-op__servico" title={card.titulo}>
            {card.titulo}
          </span>
        )}
      </div>

      {atendimento && <AtendimentoNoCard atendimento={atendimento} />}

      {(valor || canal) && (
        <div className="card-op__linha">
          {valor ? <span className="card-op__valor">{valor}</span> : <span />}
          {canal && (
            <span className="card-op__canal" data-canal={card.canal ?? undefined}>
              <span className="card-op__canal-ponto" aria-hidden="true" />
              {canal}
            </span>
          )}
        </div>
      )}

      {/*
        O risco vai escrito, com o número, e não só na cor da borda: a cor
        sozinha não chega ao leitor de tela, e "parada" e "sem interação" pedem
        conversas diferentes com quem cuida do negócio.
      */}
      {(risco || resultado) && (
        <div className="card-op__selos">
          {risco && (
            <span
              className="card-op__selo card-op__selo--risco"
              title={motivoDoRisco ? `Em risco: ${motivoDoRisco}` : undefined}
            >
              {risco}
            </span>
          )}
          {resultado && (
            <span className={`card-op__selo card-op__selo--${resultado}`}>
              {resultado === 'ganho' ? 'Ganho' : 'Perdido'}
            </span>
          )}
        </div>
      )}

      <div className="card-op__divisor" />

      {/*
        A linha de baixo tem duas faces na MESMA altura: em repouso, o que fazer
        agora; no mouse e no foco, as ações (o CSS troca uma pela outra). As duas
        estão sempre no DOM — o teclado chega nas ações, e o foco as mostra.
      */}
      <div className="card-op__agora">
        <div className="card-op__faces">
          <span className="card-op__proximo">
            {tarefa ? (
              <>
                <span className="visualmente-oculto">Próxima tarefa: </span>
                {tarefa.titulo}
                {quando && <span className="card-op__quando"> · {quando}</span>}
              </>
            ) : interacao ? (
              `Última interação ${interacao}`
            ) : (
              'Sem interação registrada'
            )}
          </span>
          <div className="card-op__barra">
            <div className="card-op__acoes">
              {/*
                O "Mover" é o `<select>` nativo, vestido de botão: a alternativa ao
                arrasto por teclado e por toque, que a §19 exige. Um menu próprio
                teria de reinventar o que o nativo já faz certo no celular.
              */}
              <label className="card-op__mover">
                <span className="visualmente-oculto">
                  Mover {card.titulo ?? 'oportunidade'} para
                </span>
                <select
                  value=""
                  disabled={!podeMover || emMovimento}
                  onChange={(e) => {
                    if (e.target.value) aoMover(card, e.target.value);
                  }}
                  title={podeMover ? 'Mover para outra etapa' : 'Criada fora do CliqCRM'}
                >
                  <option value="">Mover</option>
                  {etapas
                    .filter((e) => e.etapa !== card.etapa)
                    .map((e) => (
                      <option key={e.etapa} value={e.etapa}>
                        {e.rotulo}
                      </option>
                    ))}
                  {/*
                    Os outros funis vêm agrupados pelo nome, embaixo das etapas
                    deste: "Novo" existe em quase todo funil, e sem o grupo não se
                    sabe de qual é.
                  */}
                  {outrosFunis.map((f) => (
                    <optgroup key={f.nome} label={`Funil ${f.nome}`}>
                      {f.etapas.map((e) => (
                        <option key={e.etapa} value={e.etapa}>
                          {e.rotulo}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <Icone nome="abaixo" />
              </label>

              {card.conversa_id && (
                <button
                  type="button"
                  className="card-op__icone"
                  aria-label="Abrir conversa"
                  title="Conversa"
                  onClick={() => aoAbrirConversa(card.conversa_id!)}
                >
                  <Icone nome="atendimento" />
                </button>
              )}

              <button
                type="button"
                className="card-op__icone"
                aria-label="Nova tarefa"
                title={podeMover ? 'Criar tarefa já vinculada a esta oportunidade' : travado}
                disabled={!podeMover}
                onClick={() => aoCriarTarefa(card)}
              >
                <Icone nome="tarefa-nova" />
              </button>

              {/*
                Passar o card é possível MESMO sem conversa — é o caso que não tinha
                saída antes. Lead de portal, cadastro à mão, visita na loja: o card
                nasce sem conversa, e a atribuição só existia dentro de uma.
              */}
              <button
                type="button"
                className="card-op__icone"
                aria-label="Passar para outro responsável"
                title={podeMover ? 'Passar: a conversa ligada vai junto' : travado}
                disabled={!podeMover}
                onClick={() => definirPassando(true)}
              >
                <Icone nome="passar" />
              </button>

              {/*
                Editar, e não "Editar etapa": a etapa se muda no "Mover", e o
                contrato recusa as duas coisas no mesmo pedido. Aqui se corrige o
                que o card mostra — nome, valor, previsão e de quem é o negócio.
              */}
              <button
                type="button"
                className="card-op__icone"
                aria-label="Editar oportunidade"
                title={
                  podeMover
                    ? 'Editar nome, valor, previsão e cliente'
                    : 'Criada fora do CliqCRM: não é possível editar por aqui.'
                }
                disabled={!podeMover}
                onClick={() => aoEditar(card)}
              >
                <Icone nome="lapis" />
              </button>

              {/* Sem volta, então confirma antes: o clique só abre a pergunta. */}
              <button
                type="button"
                className="card-op__icone card-op__icone--perigo"
                aria-label="Excluir oportunidade"
                title={
                  podeMover
                    ? 'Excluir oportunidade'
                    : 'Criada fora do CliqCRM: não é possível excluir por aqui.'
                }
                disabled={!podeMover || emMovimento}
                onClick={() => aoExcluir(card)}
              >
                <Icone nome="lixeira" />
              </button>
            </div>
          </div>
        </div>
        <Responsavel
          responsavel={card.responsavel}
          aoPassar={podeMover ? () => definirPassando(true) : null}
        />
      </div>

      {card.id && (
        <Atribuir
          alvo={{ tipo: 'oportunidade', id: card.id }}
          responsavelAtual={card.responsavel_referencia_externa ?? null}
          aberto={passando}
          aoFechar={() => definirPassando(false)}
        />
      )}

      {emMovimento && <div className="card__movendo">movendo…</div>}
      {!podeMover && <div className="card__so-engine">criada fora do CliqCRM</div>}
    </Cartao>
  );
}

/**
 * O que o card é no SISAC (#1151): o serviço e o valor, o médico, para quando
 * está marcado e quando foi agendado. Lista de definições, para o leitor de
 * tela anunciar "Marcado para" junto da data.
 */
function AtendimentoNoCard({ atendimento }: { atendimento: AtendimentoDoSisac }): ReactNode {
  const marcado = quandoNaClinica(atendimento.marcado_para);
  const agendado = diaNaClinica(atendimento.agendado_em);
  const valor = atendimento.valor ? dinheiroComCentavos(atendimento.valor) : null;
  const nome = nomeDoAtendimento(atendimento);
  // O exame marcado só tem nome pela anotação da recepção: a tela diz de onde veio.
  const pelaAgenda = atendimento.servico_da_observacao;
  return (
    <dl className="card-op__atendimento">
      <div className="card-op__atendimento-linha">
        <dt className="visualmente-oculto">Atendimento</dt>
        <dd
          className="card-op__atendimento-nome"
          title={pelaAgenda ? `${nome}. ${PELA_AGENDA}` : nome}
        >
          {nome}
          {valor && (
            <span className="card-op__atendimento-valor">
              {' '}
              · {valor}
              {valorEhPrevisto(atendimento) && ' previsto'}
            </span>
          )}
        </dd>
      </div>
      {pelaAgenda && (
        <div className="card-op__atendimento-linha">
          <dt className="visualmente-oculto">Origem</dt>
          <dd className="card-op__atendimento-origem" title={PELA_AGENDA}>
            pela agenda
          </dd>
        </div>
      )}
      {atendimento.medico && (
        <div className="card-op__atendimento-linha">
          <dt className="visualmente-oculto">Médico</dt>
          <dd title={atendimento.medico}>{atendimento.medico}</dd>
        </div>
      )}
      {marcado && (
        <div className="card-op__atendimento-linha">
          <dt>Marcado para</dt>
          <dd>{marcado}</dd>
        </div>
      )}
      {agendado && (
        <div className="card-op__atendimento-linha">
          <dt>Agendado em</dt>
          <dd>{agendado}</dd>
        </div>
      )}
    </dl>
  );
}

/**
 * O dono do card, no canto do rodapé (28/09/2026).
 *
 * Foto quando há, iniciais quando não. Clicar abre o mesmo "Passar" do botão:
 * quem olha para o rosto de quem cuida do negócio é quem está pensando em
 * trocá-lo. Sem dono, o círculo tracejado — o vazio também é informação.
 */
function Responsavel({
  responsavel,
  aoPassar,
}: {
  responsavel: CardDoFunil['responsavel'];
  aoPassar: (() => void) | null;
}): ReactNode {
  const nome = responsavel ? (responsavel.nome ?? 'pessoa sem acesso ao CliqCRM') : null;
  const dica = nome ? `Responsável: ${nome}` : 'Sem responsável';
  const disco = responsavel ? (
    <Avatar nome={responsavel.nome} tamanho={26} foto={responsavel.foto_url} />
  ) : (
    <span className="card-op__sem-dono" aria-hidden="true">
      <Icone nome="pessoa" />
    </span>
  );

  if (!aoPassar) {
    return (
      <span className="card-op__dono" title={dica}>
        {disco}
        <span className="visualmente-oculto">{dica}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      className="card-op__dono"
      title={dica}
      aria-label={`${dica}. Passar para outra pessoa`}
      onClick={aoPassar}
    >
      {disco}
    </button>
  );
}
